import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useTranslations } from "use-intl";
import { Loader2 } from "lucide-react";
import { isLocale } from "@liftquest/shared/localized";
import { LanguageSwitch } from "@/components/app/language-switch";
import { useLocaleSwitch } from "@/i18n";
import { api, call } from "@/lib/api";
import { qk, useConfig } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { signIn, signUp } from "@/lib/auth-client";
import { afterSignIn } from "@/pages/layouts";
import { clearApiCache } from "@/lib/offline";

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  const t = useTranslations("auth");
  const tc = useTranslations("common");
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { locale, setLocale } = useLocaleSwitch();
  const config = useConfig();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const email = String(form.get("email")).trim();
    const password = String(form.get("password"));
    setError(null);
    if (password.length < 8) return setError(t("weakPassword"));
    setPending(true);
    const res =
      mode === "login"
        ? await signIn.email({ email, password })
        : await signUp.email(
            { email, password, name: String(form.get("name")).trim() },
            // The new account starts in the language currently shown
            { headers: { "x-locale": locale } },
          );
    if (res.error) {
      setPending(false);
      const code = res.error.code ?? "";
      setError(
        code.includes("EXISTS") ? t("exists") : mode === "login" ? t("invalid") : res.error.message || tc("error"),
      );
      return;
    }
    await clearApiCache();
    const me = await call(api.me.$get());
    // Follow the language saved on the account when signing in on a new device.
    if (mode === "login" && isLocale(me.profile.locale)) setLocale(me.profile.locale);
    qc.setQueryData(qk.me, me);
    navigate(afterSignIn(location.state), { replace: true });
  }

  // Sign-up switched off on the server (SIGNUP_ENABLED=false): only offer login.
  if (mode === "register" && config.data?.signup === false) return <Navigate to="/login" replace />;
  const offerSwitch = mode === "register" || config.data?.signup === true;

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col px-5 pb-10 pt-safe">
      <div className="flex items-center justify-between pt-4">
        <Link to="/" className="flex items-center gap-2 font-bold">
          <img src="/icon.svg" alt="" className="size-8 rounded-lg" />
          LiftQuest
        </Link>
        <LanguageSwitch />
      </div>
      <div className="mt-14">
        <h1 className="text-3xl font-black tracking-tight">
          {mode === "login" ? t("loginTitle") : t("registerTitle")}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {mode === "login" ? t("loginSubtitle") : t("registerSubtitle")}
        </p>
      </div>
      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
        {mode === "register" && (
          <div className="grid gap-1.5">
            <Label htmlFor="name">{t("name")}</Label>
            <Input id="name" name="name" required autoComplete="given-name" className="h-12 text-base" />
          </div>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor="email">{t("email")}</Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            className="h-12 text-base"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="password">{t("password")}</Label>
          <Input
            id="password"
            name="password"
            type="password"
            required
            minLength={8}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            className="h-12 text-base"
          />
          {mode === "register" && <p className="text-xs text-muted-foreground">{t("passwordHint")}</p>}
          {mode === "login" && config.data?.passwordReset && (
            <Link to="/forgot-password" className="justify-self-end text-sm font-medium text-primary">
              {t("forgotPassword")}
            </Link>
          )}
        </div>
        {error && (
          <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" className="mt-2 h-12 rounded-2xl text-base" disabled={pending}>
          {pending && <Loader2 className="animate-spin" />}
          {mode === "login" ? t("login") : t("register")}
        </Button>
      </form>
      {offerSwitch && (
        <p className="mt-6 text-center text-sm text-muted-foreground">
          {mode === "login" ? t("noAccount") : t("haveAccount")}{" "}
          <Link to={mode === "login" ? "/register" : "/login"} state={location.state} className="font-semibold text-primary">
            {mode === "login" ? t("register") : t("login")}
          </Link>
        </p>
      )}
    </main>
  );
}
